/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { AbstractRestService, identity, RestSettings } from '@gh/core-data';
import { of$ } from '@gh/rx';
import { Observable } from 'rxjs';
import { map$ } from '@gh/rx/operators';
import { TranslationService } from '@gh/core-mls';
import { IS_REGION_AU_OR_NZ } from '@gh/common-shared';

export const DATES_FILED = [
  'saleDate',
  'saleDateReported',
  'financeApprovedDate',
  'contractDate',
  'csDate',
  'councilApprovedDate',
  'authToCommenceDate',
  'contractStartDate',
  'contracturalCompletionDate',
  'slabDate',
  'slabDateReported',
  'lastAccountingDate',
  'finalClaimDate',
  'handoverDate',
  'finalizedDate',
  'notProceedingDate',
];

export const PRICES_FILED = [
  'contractAmountWithGst',
  'variationsAmountWithGst',
  'totalAmountWithGst',
  ...IS_REGION_AU_OR_NZ ? ['totalAmount'] : [],
  'estimateCost',
  'estimatedProfit',
  'profitMargin',
  'originalBudget',
  'originalContractAmount',
  'originalProfitMargin',
  'currentBudget',
  'totalInvoices',
  'drawsMadeWithGst',
  'drawsMade',
  'balanceToReceive',
  'actualProfit',
  'totalOfficeFee',
  'totalMasterFee',
  'totalOfficeVariationFee',
  'totalMasterVariationFee',
  'officePaymentDue',
  'masterPaymentDue',
];

export const DETAILS_FILED = [
  'salespersonName',
  'supervisorName',
  'jobAdminName',
  'contractType',
  'insurancePolicyNumber',
  'councilPermitNumber',
  'comments',
  'ordersCriteria',
  'overbudgetInvoices',
  'contactSalutation',
  'contactFirstName',
  'contactLastName',
  'contactPhone',
  'contactEmail',
  'csMasterName',
  'specTemplateName',
  'lastStageDrawn',
  'unsentOrders',
  'unconfirmedVariations',
  'isEstimateAuthorized',
  'outstandingMaintenanceTasks',
  'drawRuleName',
  'unitsCount',
  'drawRule',
  'specTemplateName',
  'floorArea',
];

export type Preset = {
  name: string;
  label?: string;
  columns?: string[];
};

export const TABLE_HAS_PRESETS = {
  cmList: 'cmList',
  jobs:   'jobs',
};
export const PRESETS_IDS = {
  cmList: ['PERSONAL', 'ALL', 'SIGNED', 'APPROVED', 'SLAB', 'NO_SLAB_DOWN', 'SLAB_NOT_PROCEED',
           'MASTER_DUE', 'MASTER_PAID', 'CORPORATE_DUE', 'CORPORATE_PAID'],
  jobs:   ['PERSONAL', 'ALL', 'SIGNED', 'APPROVED', 'SLAB', 'NO_SLAB_DOWN', 'SLAB_NOT_PROCEED'],
};

@Injectable()
export class GridSettingsService extends AbstractRestService {

  TABLE_HAS_CATEGORIES = [
    'cmList', 'jobs', 'jobFinancialsList', 'jobOrdersList', 'jobEstimatesList', 'drawList',
  ];

  private currentTable: string;
  private currentPreset: Preset | '';
  constructor(private settings: RestSettings, private translations: TranslationService) {
    super(settings);
  }

  pagHasCategories(): boolean {
    return !!this.currentTable && !!this.TABLE_HAS_CATEGORIES.includes(this.currentTable);
  }

  setCurrentTable(value: string): void {
    this.currentTable = value;
  }

  getCurrentTable(): string {
    return this.currentTable;
  }

  leaveTable(): void {
    this.currentTable = '';
    this.currentPreset = '';
    const presets = JSON.parse(sessionStorage.getItem('layoutPresets') || '[]') as Preset[];
    const nonPersonal = presets.filter((item) => item.name !== 'PERSONAL');
    sessionStorage.setItem('layoutPresets', JSON.stringify(nonPersonal));
  }

  setCurrentPreset(preset: Preset | ''): void {
    if (!preset) {
      this.currentPreset = '';
      return;
    }
    this.currentPreset = preset;
  }

  getCurrentPreset(): Preset | '' {
    return this.currentPreset || this.getPresets().find((item) => item.name === 'PERSONAL') || '';
  }

  setPersonalPreset(columns: string[]): void {
    const preset = {
      name:  'PERSONAL',
      label: this.translations.get('lbl_personal'),
      columns,
    };
    const presets = this.getPresets();
    const personal = presets.find((item) => item.name === 'PERSONAL');
    if (personal) {
      personal.columns = preset.columns;
      sessionStorage.setItem('layoutPresets', JSON.stringify(presets));
      return;
    }
    sessionStorage.setItem('layoutPresets', JSON.stringify([preset, ...this.getPresets()]));
  }

  getPresets(): Preset[] {
    return JSON.parse(sessionStorage.getItem('layoutPresets') || '[]') as Preset[];
  }

  getPresetsAsync = (): Observable<Preset[]> => {
    let presets = JSON.parse(sessionStorage.getItem('layoutPresets') || '[]') as Preset[];
    if (presets && presets.length) {
      return of$(presets);
    }

    return this.httpGet(`${this.jad}/presets`, identity()).pipe(map$((resp) => {
      presets = resp as Preset[];
      sessionStorage.setItem('layoutPresets', JSON.stringify(presets));
      // @ts-ignore
      return presets;
    }));
  }
}
